import React, { useState, useEffect, useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { backendClient } from '@/lib/api-client';
import { Loading } from '../shared';
import { selectedContainersFromPickerMessage, type IManageFolderPayload } from './imanageFolderPickerUtils';

interface IManageFolderPickerV2Props {
  isOpen: boolean;
  onClose: () => void;
  onFoldersSelected?: (folders: IManageFolderPayload[]) => void;
  authData: {
    access_token: string;
    customer_id: string;
    user_id?: string;
    dialog_token?: string;
  };
}

const SELECT_A_FOLDER_MESSAGE =
  'Select at least one iManage folder or workspace to sync.';

export const IManageFolderPickerV2: React.FC<IManageFolderPickerV2Props> = ({
  isOpen,
  onClose,
  onFoldersSelected,
  authData,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [isLoadingPicker, setIsLoadingPicker] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && authData?.access_token && authData?.customer_id) {
      void loadFolderPicker();
    }
  }, [isOpen, authData.access_token, authData.customer_id, authData.user_id]);

  const loadFolderPicker = async () => {
    setSelectionError(null);
    setIsLoadingPicker(true);
    try {
      const response = await backendClient.get('/imanage/folder-picker-config', {
        params: {
          access_token: authData.access_token,
          customer_id: authData.customer_id,
          user_id: authData.user_id,
        },
      });
      setIframeUrl(response.data.iframe_url);
    } catch (error: any) {
      console.error('Failed to load folder picker:', error);
      setIframeUrl(null);
      const detail = error?.response?.data?.detail;
      setSelectionError(
        typeof detail === 'string' && detail
          ? detail
          : 'Failed to load the iManage folder picker. Please try again.',
      );
    } finally {
      setIsLoadingPicker(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.match(/^https:\/\/([\w-]+\.)?cloudimanage\.com$/)) {
        return;
      }
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (data.type === 'ready') {
          return;
        }
        if (data.type === 'cancel') {
          onClose();
          return;
        }
        if (
          data.type !== 'action'
          && data.type !== 'open'
          && data.type !== 'select'
          && data.type !== 'save'
        ) {
          return;
        }

        const folders = selectedContainersFromPickerMessage(data);
        if (folders.length === 0) {
          setSelectionError(SELECT_A_FOLDER_MESSAGE);
          return;
        }
        setSelectionError(null);
        onFoldersSelected?.(folders);
        onClose();
      } catch (error) {
        console.error('Error handling postMessage:', error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isOpen, onFoldersSelected, onClose]);

  const renderContent = () => {
    if (isLoadingPicker) {
      return (
        <div className="flex items-center justify-center h-[80vh]">
          <Loading text="Loading iManage folder picker" />
        </div>
      );
    }

    if (iframeUrl) {
      return (
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          className="w-full h-[95vh] border-0"
          title="iManage Folder Picker"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        />
      );
    }

    return null;
  };

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-100000 bg-black/10 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-[50%] top-[50%] z-100001 max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] translate-x-[-50%] translate-y-[-50%] border border-stone-200 bg-white shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-lg overflow-hidden p-0"
        >
          <DialogPrimitive.Title className="sr-only">
            iManage folder picker
          </DialogPrimitive.Title>
          {selectionError && (
            <div className="absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 shadow-md">
              {selectionError}
            </div>
          )}
          {renderContent()}
          <DialogPrimitive.Close className="absolute right-3 top-3 z-10 rounded-xs p-1.5 text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-gray-400 focus:ring-offset-2">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
